import { useState } from "react";
import { motion } from "framer-motion";
import { Star, Trash2, ChefHat, Utensils } from "lucide-react";
import { loadFavorites, removeFavorite } from "@/lib/mbStorage";
import { RecipeSheet } from "@/components/RecipeSheet";

const fmtDate = (ts) =>
  new Date(ts).toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" });

export const Preferiti = () => {
  const [favorites, setFavorites] = useState(loadFavorites());
  const [openFav, setOpenFav] = useState(null);

  const remove = (id) => setFavorites(removeFavorite(id));

  return (
    <div className="w-full" data-testid="screen-preferiti">
      <div className="text-center mb-4 mt-2">
        <h2 className="font-display text-xl font-bold text-slate-800 flex items-center justify-center gap-2">
          <Star size={18} className="fill-[#f59e0b] text-[#f59e0b]" /> I miei Preferiti
        </h2>
        <p className="text-slate-400 text-xs mt-1">Le ricette che hai salvato</p>
      </div>

      {favorites.length === 0 && (
        <div className="text-center py-16 text-slate-400" data-testid="favorites-empty">
          <Star size={30} className="mx-auto mb-3 text-slate-300" />
          <p className="text-sm italic">Nessuna ricetta salvata.</p>
          <p className="text-[12px] mt-1">Apri una ricetta dal Diario e tocca la stella ⭐</p>
        </div>
      )}

      <div className="flex flex-col gap-3" data-testid="favorites-list">
        {favorites.map((fav, i) => (
          <motion.div
            key={fav.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="bg-white rounded-2xl border border-slate-200 shadow-[0_2px_8px_rgba(0,0,0,0.03)] overflow-hidden"
            data-testid="favorite-card"
          >
            <div className="flex items-center gap-3 p-3">
              {fav.image ? (
                <img src={fav.image} alt={fav.dish_name} className="w-16 h-16 rounded-xl object-cover shrink-0" />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                  <Utensils size={22} className="text-slate-300" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="font-display text-sm font-bold text-slate-800 leading-tight line-clamp-2">
                  {fav.recipe?.title || fav.dish_name}
                </h3>
                <p className="text-[11px] text-slate-400 mt-0.5">Salvata il {fmtDate(fav.savedAt)}</p>
                <div className="flex items-center gap-2 mt-1.5 text-[10px] text-slate-400">
                  <span className="flex items-center gap-1"><ChefHat size={11} /> {fav.recipe?.ingredients?.length || 0} ingredienti</span>
                  <span>·</span>
                  <span>{fav.recipe?.steps?.length || 0} passaggi</span>
                </div>
              </div>
            </div>
            <div className="flex border-t border-slate-100">
              <button
                onClick={() => setOpenFav(fav)}
                data-testid={`view-favorite-${fav.id}`}
                className="flex-1 py-2.5 text-[13px] font-semibold text-[#2b90ef] flex items-center justify-center gap-1.5 active:bg-slate-50 transition-colors"
              >
                <ChefHat size={15} /> Vedi ricetta
              </button>
              <div className="w-px bg-slate-100" />
              <button
                onClick={() => remove(fav.id)}
                data-testid={`remove-favorite-${fav.id}`}
                className="px-5 py-2.5 text-slate-400 hover:text-red-500 flex items-center justify-center active:bg-slate-50 transition-colors"
              >
                <Trash2 size={15} />
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      <RecipeSheet
        meal={openFav ? { id: openFav.id, dish_name: openFav.dish_name, image: openFav.image } : null}
        presetRecipe={openFav?.recipe}
        onClose={() => {
          setOpenFav(null);
          setFavorites(loadFavorites());
        }}
      />
    </div>
  );
};
